import { EPrescriptionType } from '@/utils/enums/goods';
import { isPrescriptionPdMeasurementTypeSupported } from './availability';
import type { PdMeasurementRecordSource, PrescriptionPdMeasurementRisk } from './types';

interface OnlinePdMeasurement {
  recordId?: string;
  onlinePdMm: number;
  entrySource?: PdMeasurementRecordSource;
}

interface PrescriptionPdValue {
  is_two_pd?: boolean | string | number;
  pd: string;
  pd_l: string;
  pd_r: string;
}

interface PrescriptionPowerValue {
  od_sph?: string;
  os_sph?: string;
  od_cyl?: string;
  os_cyl?: string;
}

function isPrescriptionPowerInRange(value: string | undefined, min: number, max: number) {
  if (!value?.trim()) return false;
  const parsedValue = value === 'PL' ? 0 : Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= min && parsedValue <= max;
}

function isPrescriptionCylinderSupported(value: string | undefined) {
  if (value?.trim() === 'SPH/DS') return true;

  return isPrescriptionPowerInRange(value, -2.5, -0.25) || isPrescriptionPowerInRange(value, 0, 0);
}

export function resolvePrescriptionPdMeasurementRisk(
  prescription: PrescriptionPowerValue,
): PrescriptionPdMeasurementRisk {
  const sphIsSupported = [prescription.od_sph, prescription.os_sph].every((value) =>
    isPrescriptionPowerInRange(value, -6, 3),
  );
  const cylIsSupported = [prescription.od_cyl, prescription.os_cyl].every(
    isPrescriptionCylinderSupported,
  );

  return sphIsSupported && cylIsSupported ? 'standard' : 'requires-confirmation';
}

function isTwoPdSelected(value: PrescriptionPdValue['is_two_pd']): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

interface ResolveHistoricalOnlinePdPrefillInput {
  measurement: OnlinePdMeasurement | null;
  prescriptionType: EPrescriptionType;
  prescription: PrescriptionPdValue;
  hasHistoricalPrescription: boolean;
  hasSelectedPrescription: boolean;
  minPd: number;
  maxPd: number;
  isCurrentMeasurement?: boolean;
}

export type OnlinePdPrescriptionAction =
  | { kind: 'none' }
  | { kind: 'show-history-notice'; pd: string }
  | { kind: 'prefill-current-measurement'; pd: string };

export function resolveHistoricalOnlinePdPrefill({
  measurement,
  prescriptionType,
  prescription,
  hasHistoricalPrescription,
  hasSelectedPrescription,
  minPd,
  maxPd,
}: ResolveHistoricalOnlinePdPrefillInput): string | undefined {
  if (
    !measurement ||
    !isPrescriptionPdMeasurementTypeSupported(prescriptionType) ||
    hasHistoricalPrescription ||
    hasSelectedPrescription ||
    isTwoPdSelected(prescription.is_two_pd) ||
    Boolean(prescription.pd.trim()) ||
    Boolean(prescription.pd_l.trim()) ||
    Boolean(prescription.pd_r.trim())
  ) {
    return undefined;
  }

  const { onlinePdMm } = measurement;
  if (
    !Number.isFinite(onlinePdMm) ||
    !Number.isFinite(minPd) ||
    !Number.isFinite(maxPd) ||
    minPd > maxPd ||
    onlinePdMm < minPd ||
    onlinePdMm > maxPd
  ) {
    return undefined;
  }

  return onlinePdMm.toFixed(2);
}

export function resolveOnlinePdPrescriptionAction(
  input: ResolveHistoricalOnlinePdPrefillInput,
): OnlinePdPrescriptionAction {
  const { measurement, minPd, maxPd, prescriptionType } = input;
  if (input.isCurrentMeasurement && measurement) {
    const { onlinePdMm } = measurement;
    if (
      isPrescriptionPdMeasurementTypeSupported(prescriptionType) &&
      Number.isFinite(onlinePdMm) &&
      Number.isFinite(minPd) &&
      Number.isFinite(maxPd) &&
      minPd <= maxPd &&
      onlinePdMm >= minPd &&
      onlinePdMm <= maxPd
    ) {
      return { kind: 'prefill-current-measurement', pd: onlinePdMm.toFixed(2) };
    }
  }

  if (measurement?.entrySource === 'lens-prescription-pd') {
    return { kind: 'none' };
  }

  const pd = resolveHistoricalOnlinePdPrefill(input);
  if (!pd) return { kind: 'none' };

  return { kind: 'show-history-notice', pd };
}
